// src/services/api.js
import * as mock from "./mockApi";
import { authFetch } from "./http";
import { login as realLogin, updateMe as realUpdateMe } from "./realApi";

// Optional API base URL (same default as realApi.js). Empty = same origin.
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Toggle only the login path (real vs mock) via env
// .env.local → REACT_APP_USE_REAL_LOGIN=1
const USE_REAL_LOGIN = process.env.REACT_APP_USE_REAL_LOGIN === "1";

/** Login: uses real backend when flag is on, otherwise mock */
export const login = USE_REAL_LOGIN ? realLogin : mock.login;

export async function updateMyProfile(patch) {
  return realUpdateMe(patch);
}

/**
 * Register a user after KYC flow. Expects { email, password, passwordConfirm, token }.
 * This endpoint is public and should be implemented on the backend to validate the
 * third-party token and create the account. We follow the same credentials include
 * pattern as login so any cookies set by the server are preserved.
 */
export async function registerAfterKyc({ email, password, passwordConfirm } = {}) {
  const url = `${API_URL}/auth/register`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password, passwordConfirm }),
  });

  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    const msg = data?.error || `Registration failed (${res.status})`;
    throw new Error(msg);
  }

  return data;
}

/**
 * Request a password reset email.
 * Body: { email }
 * Always returns success regardless of whether the email exists.
 */
export async function requestPasswordReset({ email } = {}) {
  const url = `${API_URL}/auth/password-reset/request`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email }),
  });

  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    throw new Error(data?.error || `Request failed (${res.status})`);
  }

  return data;
}

/**
 * Confirm a password reset using the one-time token from the email link.
 * Body: { token, password, password_confirm }
 */
export async function confirmPasswordReset({ token, password, password_confirm } = {}) {
  const url = `${API_URL}/auth/password-reset/confirm`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ token, password, password_confirm }),
  });

  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    const msg = data?.error || `Reset failed (${res.status})`;
    throw new Error(msg);
  }

  return data;
}

/**
 * Activate account using an account_activation token.
 * Body: { token, password }
 */
export async function activateAccount({ token, password } = {}) {
  const url = `${API_URL}/auth/activate-account`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ token, password }),
  });

  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    const msg = data?.error || `Activation failed (${res.status})`;
    throw new Error(msg);
  }

  return data;
}

/** Assignments (REAL) – ignores any args; backend gets user from JWT */
export async function listMyAssignments() {
  const res = await authFetch("/assignments/my");
  if (!res.ok) throw new Error("Failed to load assignments");
  return res.json();
}

/** Training detail (EMPLOYEE SCOPE) – include SAS window to match backend */
export async function getTrainingById(slug) {
  const res = await authFetch(`/trainings/${slug}?sasMinutes=60`);
  if (!res.ok) throw new Error("Failed to load training");
  return res.json();
}

/** Fetch a JSON quiz file (contentUrl). */
export async function fetchQuizJson(contentUrl) {
  const res = await fetch(contentUrl, { credentials: "omit" });
  if (!res.ok) throw new Error("Failed to load quiz file");
  return res.json(); // { id, type:'quiz', title, questions:[...] }
}

/** Quiz submit (REAL). userId is unused; pass optional stepId for multi-quiz trainings */
export async function submitQuiz(_userId, trainingId, answers, stepId) {
  const res = await authFetch("/quiz/submit", {
    method: "POST",
    body: JSON.stringify({ trainingId, answers, stepId }),
  });
  if (!res.ok) throw new Error("Quiz submit failed");
  return res.json();
}

// Certificate: guard
/*export async function isCertificateAllowed(slug) {
  const res = await authFetch(`/certificate/${slug}/allowed`);
  if (!res.ok) return { allowed: false, status: "error" };
  return res.json(); // { allowed, status }
}*/

// Certificate: real upload (multipart/form-data with `file` field)
/*export async function uploadCertificate(slug, fileMeta) {
  // fileMeta should be a File/Blob from <input type="file" />
  const fd = new FormData();
  if (fileMeta) {
    // third arg preserves original filename
    fd.append("file", fileMeta, fileMeta.name || "certificate");
  }

  const res = await authFetch(`/certificate/${slug}/upload`, {
    method: "POST",
    body: fd,
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Upload failed");
  }
  return res.json(); // e.g. { ok: true, status: "completed", ... }
}*/

export async function getProgress(slug) {
  const res = await authFetch(`/progress/${slug}`);
  if (!res.ok) throw new Error("Failed to load progress");
  return res.json(); // { lastStepIndex }
}

export async function saveProgress(slug, stepIndex) {
  const res = await authFetch(`/progress/${slug}`, {
    method: "POST",
    body: JSON.stringify({ stepIndex }),
  });
  if (!res.ok) throw new Error("Failed to save progress");
  return res.json(); // { ok, lastStepIndex }
}

export async function getTeamProgress() {
  const res = await authFetch("/team/progress");
  if (!res.ok) throw new Error("Failed to load team progress");
  return res.json(); // [{ userId, name, email, trainings:[{trainingId,title,status}], dueCount, completedCount }]
}

/**
 * Get a short-lived SAS URL for the generated (unsigned) certificate PDF.
 * Optionally pass { userId } to fetch for someone else (manager/org admin/platform).
 * Returns a plain URL string (we normalize common response shapes).
 */
export async function getCertificateDownload(slug, opts = {}) {
  const params = new URLSearchParams();
  if (opts.kind) params.set("kind", String(opts.kind));
  if (opts.userId) params.set("userId", String(opts.userId));
  if (opts.sasMinutes) params.set("sasMinutes", String(opts.sasMinutes));
  const q = params.toString() ? `?${params.toString()}` : "";

  const res = await authFetch(`/certificate/${slug}/download${q}`);
  if (!res.ok) throw new Error("Failed to prepare certificate download");

  // Be resilient to backend shapes:
  // e.g. { url: "...sas..." } or { sas: "...sas..." } or raw string
  const data = await res.json().catch(() => ({}));
  const url = typeof data === "string"
    ? data
    : data.url || data.sas || data.href || "";

  if (!url) throw new Error("No certificate URL returned");
  return url;
}


/* ------------------------------- ORG ADMIN -------------------------------- */
export async function adminListUsers({
  q = "",
  page = 1,
  pageSize = 20,
  role = "",
  isManager = "",
  includeDeactivated = false,
  tenantId = "",
  worksiteId = "",
} = {}) {
  const params = new URLSearchParams();
  if (q !== "") params.set("q", q);
  params.set("page", String(page));
  // Support both camelCase and snake_case page size params on the backend
  params.set("pageSize", String(pageSize));
  params.set("page_size", String(pageSize));
  if (role !== "") params.set("role", role);
  if (isManager !== "") params.set("isManager", String(isManager));
  params.set("includeDeactivated", String(includeDeactivated));
  if (tenantId !== "") params.set("tenant_id", tenantId);
  if (worksiteId !== "") params.set("worksite_id", worksiteId);

  const res = await authFetch(`/admin/users?${params.toString()}`);
  if (!res.ok) throw new Error("Failed to list users");
  const data = await res.json();
  const items = Array.isArray(data) ? data : (data.items || []);

  // Normalize pagination metadata. Backend may return legacy `meta` or new top-level pagination fields.
  let meta = null;
  if (!Array.isArray(data)) {
    const p = (data.page ?? (data.meta && data.meta.page) ?? page) || 1;
    const ps = (data.page_size ?? data.pageSize ?? (data.meta && data.meta.pageSize) ?? pageSize) || pageSize;
    const total = Number(data.total ?? (data.meta && data.meta.total) ?? 0);
    const total_pages = Number(data.total_pages ?? data.pages ?? Math.ceil(total / (ps || 1)));
    const has_next = typeof data.has_next !== "undefined" ? !!data.has_next : (p < total_pages);
    const has_prev = typeof data.has_prev !== "undefined" ? !!data.has_prev : (p > 1);
    meta = { page: Number(p), page_size: Number(ps), total: total, total_pages: total_pages, has_next, has_prev };
  }

  return { items, meta };
}

export async function listTenants() {
  const res = await authFetch("/platform/tenants");
  if (!res.ok) throw new Error("Failed to list tenants");
  return res.json();
}

export async function listWorksites({ tenantId = "" } = {}) {
  const params = new URLSearchParams();
  if (tenantId !== "") params.set("tenant_id", tenantId);
  const res = await authFetch(`/admin/worksites?${params.toString()}`);
  if (!res.ok) throw new Error("Failed to list worksites");
  return res.json();
}

export async function adminGetUser(userId) {
  const res = await authFetch(`/admin/users/${userId}`);
  if (res.status === 404) throw Object.assign(new Error("not_found"), { status: 404 });
  if (res.status === 403) throw Object.assign(new Error("forbidden"), { status: 403 });
  if (!res.ok) throw new Error("Failed to load user");
  return res.json(); // { ok, user }
}

/**
 * Platform admin: trigger CertaSign KYC sync for a single user.
 * Body: { user_id, dry_run, overwrite }
 * Returns backend JSON response (may include `status`, `found`, `kyc_status`, `updated_fields`, `error`).
 */
export async function platformCertaSignKycSync({ userId, dryRun = false, overwrite = false } = {}) {
  const body = { user_id: userId, dry_run: dryRun, overwrite };
  const res = await authFetch(`/platform/certasign/kyc-sync`, {
    method: "POST",
    body: JSON.stringify(body),
  });

  if (res.status === 404) throw Object.assign(new Error("not_found"), { status: 404 });
  if (res.status === 403) throw Object.assign(new Error("forbidden"), { status: 403 });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let data = {};
    try { data = text ? JSON.parse(text) : {}; } catch (e) { /* ignore */ }
    const msg = data?.message || data?.error || text || `CertaSign sync failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return res.json();
}

export async function adminGetUserTrainingHistory(userId) {
  const res = await authFetch(`/admin/users/${userId}/training-history`);
  if (res.status === 404) throw Object.assign(new Error("not_found"), { status: 404 });
  if (res.status === 403) throw Object.assign(new Error("forbidden"), { status: 403 });
  if (!res.ok) throw new Error("Failed to load training history");
  return res.json(); // { ok, user, assignments }
}

export async function getOrgAdminDashboardStats({ worksiteId = "" } = {}) {
  const params = new URLSearchParams();
  if (worksiteId) params.set("worksite_id", worksiteId);
  const qs = params.toString();
  const res = await authFetch(`/admin/dashboard/stats${qs ? `?${qs}` : ""}`);
  if (!res.ok) throw new Error("Failed to load dashboard stats");
  return res.json(); // { ok, tenant, scope, stats }
}

export async function adminCreateUser({
  email,
  name,
  password,
  isManager = false,
}) {
  const res = await authFetch("/admin/users", {
    method: "POST",
    body: JSON.stringify({ email, name, password, isManager }),
  });
  if (!res.ok) throw new Error("Failed to create user");
  return res.json();
}

export async function adminUpdateUser(
  userId,
  { name, email, password, isManager, addRoles, removeRoles, setRoles, job_title, start_date, phone, address, birth_place }
) {
  const body = { name, email, password, isManager, addRoles, removeRoles, setRoles };
  if (typeof job_title !== "undefined") body.job_title = job_title;
  if (typeof start_date !== "undefined") body.start_date = start_date;
  if (typeof phone !== "undefined") body.phone = phone;
  if (typeof address !== "undefined") body.address = address;
  if (typeof birth_place !== "undefined") body.birth_place = birth_place;

  const res = await authFetch(`/admin/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to update user");
  const data = await res.json();
  return data;
}

export async function adminDeleteUser(userId) {
  const res = await authFetch(`/admin/users/${userId}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete user");
  return res.json();
}

/** Org Admin: assign a training to one or more users */
export async function adminAssignTraining({
  trainingSlug,
  trainingId,
  assigneeUserIds,
  status = "NOT_STARTED",
}) {
  const res = await authFetch(`/admin/assignments`, {
    method: "POST",
    body: JSON.stringify({ trainingSlug, trainingId, assigneeUserIds, status }),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(t || "Failed to assign training");
  }
  return res.json(); // { ok: true, ... }
}

/** Org Admin: unassign a training from one or more users */
export async function adminUnassignTraining({
  trainingSlug,
  trainingId,
  assigneeUserIds,
  force = false,
}) {
  const res = await authFetch(`/admin/assignments`, {
    method: "DELETE",
    body: JSON.stringify({ trainingSlug, trainingId, assigneeUserIds, force }),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(t || "Failed to unassign training");
  }
  return res.json(); // { ok: true, ... }
}

/*export async function listMyCertificates({ sasMinutes = 60 } = {}) {
  const res = await authFetch(
    `/certificates/my?sasMinutes=${encodeURIComponent(sasMinutes)}`
  );
  if (!res.ok) throw new Error("Failed to load certificates");
  return res.json(); // [{ training:{slug,title}, uploadedUrl, uploadedUrlSas?, createdAt }]
}*/

/** Certificates for a specific user (authZ handled by backend) */
/*export async function listUserCertificates(userId, { sasMinutes = 60 } = {}) {
  const res = await authFetch(
    `/certificates/user/${userId}?sasMinutes=${encodeURIComponent(sasMinutes)}`
  );
  if (!res.ok) throw new Error("Failed to load user certificates");
  return res.json();
}*/

/** Manager: list current user's direct reports (minimal profile) */
export async function listDirectReports() {
  const res = await authFetch(`/team/reports`);
  if (!res.ok) throw new Error("Failed to load direct reports");
  return res.json(); // [{ id, name, email, ... }]
}

export async function listTrainings({
  q = "",
  tenantId = "",
  visibility = "",
  page = 1,
  pageSize = 50,
} = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (tenantId) params.set("tenantId", tenantId);
  if (visibility !== "") params.set("visibility", visibility);
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  const res = await authFetch(`/trainings?${params.toString()}`);
  if (!res.ok) throw new Error("Failed to load trainings");
  return res.json(); // { items, meta }
}

/* ---------------------------- PLATFORM ADMIN ------------------------------ */
/*export async function platformListTrainings({
  q = "",
  tenantId = "",
  visibility = "",
  page = 1,
  pageSize = 50,
} = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (tenantId) params.set("tenantId", tenantId);
  if (visibility !== "") params.set("visibility", visibility);
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  const res = await authFetch(`/trainings?${params.toString()}`);
  if (!res.ok) throw new Error("Failed to load trainings");
  return res.json(); // { items, meta }
}*/

/*export async function platformListTrainings({
  q = "",
  tenantId = "",
  visibility = "",
  page = 1,
  pageSize = 20,
} = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (tenantId) params.set("tenantId", tenantId);
  if (visibility !== "") params.set("visibility", visibility);
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));

  const res = await authFetch(`/platform/trainings?${params.toString()}`);
  if (!res.ok) throw new Error("Failed to list trainings");
  const data = await res.json();
  // backend returns { items, meta }; fall back to array if ever unpaginated
  return {
    items: Array.isArray(data) ? data : (data.items ?? []),
    meta: Array.isArray(data) ? null : (data.meta ?? null),
  };
}*/

/** Get training by ID (platform scope) */
export async function platformGetTraining(id, sasMinutes = 60) {
  const res = await authFetch(
    `/platform/trainings/${id}?sasMinutes=${sasMinutes}`
  );
  if (!res.ok) throw new Error("Failed to load training (platform)");
  const data = await res.json();
  // backend returns { ok, sasExpiry, training: {...} }
  // return the training object directly for the UI
  return data?.training ?? data;
}

/** Create training */
export async function platformCreateTraining(payload) {
  // expected:
  // { slug, title, description, tenantId: null|number, visibility: boolean, steps: [...] }
  const res = await authFetch(`/platform/trainings`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Failed to create training");
  return res.json();
}

/** Update training metadata */
export async function platformUpdateTraining(id, payload) {
  // { title?, description?, slug?, visibility?, tenantId? }
  const res = await authFetch(`/platform/trainings/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Failed to update training");
  return res.json();
}

/** Delete training */
export async function platformDeleteTraining(id, { force = false } = {}) {
  const res = await authFetch(
    `/platform/trainings/${id}?force=${force ? "true" : "false"}`,
    {
      method: "DELETE",
    }
  );
  if (!res.ok) throw new Error("Failed to delete training");
  return res.json();
}

/** Replace content (all steps) */
export async function platformReplaceContent(
  id,
  { deleteOrphans = false, steps = [] }
) {
  const res = await authFetch(`/platform/trainings/${id}/content`, {
    method: "PATCH",
    body: JSON.stringify({ deleteOrphans, steps }),
  });
  if (!res.ok) throw new Error("Failed to replace content");
  return res.json();
}

/** Append step */
export async function platformAppendStep(id, step) {
  const res = await authFetch(`/platform/trainings/${id}/steps`, {
    method: "POST",
    body: JSON.stringify(step), // { type, title, filename?, contentBase64? | questions? }
  });
  if (!res.ok) throw new Error("Failed to append step");
  return res.json();
}

/** Remove step by ordinal (1-based in Postman examples) */
export async function platformRemoveStep(
  id,
  ordinal,
  { deleteFiles = false } = {}
) {
  const res = await authFetch(
    `/platform/trainings/${id}/steps/${ordinal}?deleteFiles=${
      deleteFiles ? "true" : "false"
    }`,
    {
      method: "DELETE",
    }
  );
  if (!res.ok) throw new Error("Failed to remove step");
  return res.json();
}

/** Insert step at ordinal */
export async function platformInsertStep(id, ordinal, step) {
  const res = await authFetch(
    `/platform/trainings/${id}/steps/${ordinal}/insert`,
    {
      method: "POST",
      body: JSON.stringify(step),
    }
  );
  if (!res.ok) throw new Error("Failed to insert step");
  return res.json();
}

/** Reorder steps */
export async function platformReorderSteps(id, from, to) {
  const res = await authFetch(`/platform/trainings/${id}/steps/reorder`, {
    method: "PATCH",
    body: JSON.stringify({ from, to }),
  });
  if (!res.ok) throw new Error("Failed to reorder steps");
  return res.json();
}

/** Org Admin: assign a manager -> report relationship */
export async function adminAssignManager({
  managerId,
  reportId,
  autoPromoteManager = true,
  replaceExisting = true,
}) {
  const res = await authFetch(`/admin/team/assign`, {
    method: "POST",
    body: JSON.stringify({
      managerId,
      reportId,
      autoPromoteManager,
      replaceExisting,
    }),
  });
  if (!res.ok) throw new Error("Failed to assign manager");
  return res.json(); // backend may return { ok: true } or relationship info
}

/** Org Admin: unassign a manager from a report */
export async function adminUnassignManager({ managerId, reportId }) {
  const res = await authFetch(`/admin/team/assign`, {
    method: "DELETE",
    body: JSON.stringify({ managerId, reportId }),
  });
  if (!res.ok) throw new Error("Failed to unassign manager");
  return res.json(); // { ok: true }
}

// Admin: list direct reports for a specific manager (requires backend support)
export async function adminListReportsForManager(managerId) {
  const res = await authFetch(
    `/admin/team/reports?managerId=${encodeURIComponent(managerId)}`
  );
  if (!res.ok)
    throw new Error(await res.text().catch(() => "Failed to load reports"));
  return res.json(); // [{ id, name, email }]
}


/* ----------------------------- CERTIFICATES ----------------------------- */

/**
 * Check if the current user is allowed to upload a certificate for this training.
 * Backend returns: { allowed, status, stepsCount, lastStepIndex }
 */
export async function isCertificateAllowed(slug) {
  const res = await authFetch(`/certificate/${slug}/allowed`);
  if (!res.ok) throw new Error("Failed to check certificate allowance");
  return res.json();
}

/**
 * Generate an UNSIGNED certificate for a specific kind ("SSM" | "SU").
 * POST /certificate/:slug/:kind/generate -> { ok, kind, generatedUrl, generatedUrlSas }
 */
export async function generateCertificate(slug, kind) {
  if (!kind) throw new Error("Certificate kind is required");
  const res = await authFetch(`/certificate/${slug}/${kind}/generate`, {
    method: "POST",
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Failed to generate certificate");
  }
  return res.json();
}

/**
 * Request signing/delivery of the certificate for the current user.
 * POST /certificate/:slug/sign -> { ok, message }
 */
export async function signCertificate(slug) {
  if (!slug) throw new Error("Training slug is required");
  const res = await authFetch(`/certificate/${slug}/sign`, { method: "POST" });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    let data = {};
    try { data = JSON.parse(txt); } catch { /* keep data empty */ }
    throw new Error(data?.error || data?.message || txt || "Failed to request certificate signing/delivery");
  }
  return res.json();
}

/** GET signing config for (training, kind). Returns null when none exists. */
export async function getSigningConfig(slug, kind) {
  const res = await authFetch(`/training/${encodeURIComponent(slug)}/signing-config?kind=${encodeURIComponent(kind)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error("Failed to load signing config");
  return res.json();
}

/** PUT (upsert) signing config. Returns saved config. */
export async function saveSigningConfig(slug, config) {
  const res = await authFetch(`/training/${encodeURIComponent(slug)}/signing-config`, {
    method: "PUT",
    body: JSON.stringify(config),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    let data = {};
    try { data = JSON.parse(txt); } catch { /* keep data empty */ }
    throw new Error(data?.message || data?.error || txt || "Failed to save signing config");
  }
  return res.json();
}

/** DELETE signing config for (training, kind). */
export async function deleteSigningConfig(slug, kind) {
  const res = await authFetch(
    `/training/${encodeURIComponent(slug)}/signing-config?kind=${encodeURIComponent(kind)}`,
    { method: "DELETE" }
  );
  if (!res.ok) throw new Error("Failed to delete signing config");
  return res.json();
}

/** Check current user's profile completion status (backend: GET /auth/user/completion_status) */
export async function getUserCompletionStatus() {
  const res = await authFetch(`/auth/user/completion_status`);
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Failed to check user completion status");
  }
  return res.json();
}

/**
 * Get onboarding status for the current user.
 * GET /api/me/onboarding-status
 * Returns the parsed JSON or throws on network/HTTP error.
 */
export async function getOnboardingStatus() {
  const res = await authFetch(`/me/onboarding-status`);
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Failed to load onboarding status");
  }
  // Parse JSON and normalize backend shapes. Some backends return:
  // { ok: true, onboarding: { ... } } while others return the onboarding
  // object directly. Normalize to always return the onboarding object.
  const data = await res.json();
  return data?.onboarding ?? data;
}

/** Route declaration: options for the current user (employers, worksites, defaults) */
export async function getRouteDeclarationOptions() {
  const res = await authFetch(`/me/route-declaration/options`);
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Failed to load route declaration options");
  }
  return res.json();
}

/** Get current user's route declaration (may be null) */
export async function getRouteDeclaration() {
  const res = await authFetch(`/me/route-declaration`);
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Failed to load route declaration");
  }
  return res.json();
}

/** Submit route declaration for platform-admin approval */
export async function submitRouteDeclarationForApproval(payload = {}) {
  // Some backends expect `tenant_id` while others use `employer_tenant_id`.
  // For compatibility, include both keys when available.
  const body = { ...payload };
  if (body.employer_tenant_id !== undefined && body.tenant_id === undefined) {
    body.tenant_id = body.employer_tenant_id;
  }
  if (body.tenant_id !== undefined && body.employer_tenant_id === undefined) {
    body.employer_tenant_id = body.tenant_id;
  }

  const res = await authFetch(`/me/route-declaration/submit-for-approval`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    // ignore
  }
  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Failed to submit route declaration (${res.status})`;
    throw new Error(msg);
  }
  return data;
}

/* ------------------------- EMPLOYEE: Route Declaration PDF Actions ------------------------- */

export async function generateMyRouteDeclarationPdf() {
  const res = await authFetch(`/me/route-declaration/generate-pdf`, { method: "POST" });
  const text = await res.text().catch(() => "");
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch { /* ignore */ }
  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Failed to generate PDF (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data.revision ?? data;
}

export async function listMyRouteDeclarationRevisions() {
  const res = await authFetch(`/me/route-declaration/revisions`);
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Failed to load revisions");
  }
  return res.json();
}

export async function downloadMyRouteDeclarationRevision(revisionId, variant = "unsigned") {
  const q = variant ? `?variant=${encodeURIComponent(variant)}` : "";
  const res = await authFetch(`/me/route-declaration/revisions/${encodeURIComponent(revisionId)}/download${q}`);
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    let data = {};
    try { data = txt ? JSON.parse(txt) : {}; } catch { /* ignore */ }
    const msg = data?.error || data?.message || txt || `Failed to download (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match ? match[1] : `declaratie_traseu_${revisionId}_${variant}.pdf`;
  return { blob, filename };
}

export async function sendMyRouteDeclarationForSignature(revisionId) {
  const res = await authFetch(`/me/route-declaration/revisions/${encodeURIComponent(revisionId)}/send-for-signature`, { method: "POST" });
  const text = await res.text().catch(() => "");
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch { /* ignore */ }
  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Failed to send for signature (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data.revision ?? data;
}

/* ------------------------- PLATFORM: Route Declarations ------------------------- */
/** List platform route declarations (platform admin). Accepts optional filters: { status, page, pageSize } */
export async function getPlatformRouteDeclarations({ status = "", page = 1, pageSize = 50, userId = "" } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  if (userId) params.set("user_id", String(userId));

  const res = await authFetch(`/platform/route-declarations?${params.toString()}`);
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Failed to load route declarations");
  }
  return res.json();
}

/** Get single route declaration by id (platform admin) */
export async function getPlatformRouteDeclaration(id) {
  if (!id) throw new Error("id is required");
  const res = await authFetch(`/platform/route-declarations/${encodeURIComponent(id)}`);
  if (res.status === 404) throw Object.assign(new Error("not_found"), { status: 404 });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Failed to load route declaration");
  }
  return res.json();
}

/** Approve a pending route declaration (platform admin) */
export async function approvePlatformRouteDeclaration(id) {
  if (!id) throw new Error("id is required");
  const res = await authFetch(`/platform/route-declarations/${encodeURIComponent(id)}/approve`, {
    method: "POST",
  });
  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    // ignore
  }
  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Failed to approve route declaration (${res.status})`;
    throw new Error(msg);
  }
  return data;
}

/**
 * Update a pending route declaration (platform admin).
 * PATCH /platform/route-declarations/:id
 */
export async function updatePlatformRouteDeclaration(id, payload = {}) {
  if (!id) throw new Error("id is required");
  // Normalize tenant/employer keys for compatibility with different backends
  const body = { ...payload };
  // Avoid sending `tenant_id` for platform PATCH requests: some backends reject it as a forbidden field.
  // If the client accidentally provided `tenant_id`, map it to `employer_tenant_id` so the server
  // receives the canonical key, but do NOT set `tenant_id` from `employer_tenant_id`.
  if (body.tenant_id !== undefined && body.employer_tenant_id === undefined) {
    body.employer_tenant_id = body.tenant_id;
  }

  const res = await authFetch(`/platform/route-declarations/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    // ignore
  }
  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Failed to update route declaration (${res.status})`;
    throw new Error(msg);
  }
  return data;
}

/* ------------------------- PLATFORM: SSM Document Revisions ------------------------- */
/** Create/generate an unsigned SSM document revision from a route declaration (platform admin) */
export async function createPlatformSsmRevision(declarationId) {
  if (!declarationId) throw new Error("declarationId is required");
  const res = await authFetch(`/platform/route-declarations/${encodeURIComponent(declarationId)}/ssm-document-revision`, {
    method: "POST",
  });
  const text = await res.text().catch(() => "");
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch { /* ignore */ }
  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Failed to create revision (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data.revision ?? data;
}

/** Create/generate an unsigned SU document revision for a user (platform admin) */
export async function createPlatformSuRevision(userId) {
  if (!userId) throw new Error("userId is required");
  const res = await authFetch(`/platform/users/${encodeURIComponent(userId)}/su-document-revision`, {
    method: "POST",
  });
  const text = await res.text().catch(() => "");
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch { /* ignore */ }
  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Failed to create SU revision (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data.revision ?? data;
}

/** List SU document revisions for a user (platform admin) */
export async function listPlatformUserSuRevisions(userId) {
  if (!userId) throw new Error("userId is required");
  const res = await authFetch(`/platform/users/${encodeURIComponent(userId)}/su-document-revisions`);
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Failed to load SU revisions");
  }
  return res.json(); // { ok: true, items: [...], total }
}

/** List SSM document revisions for a user (platform admin) */
export async function listPlatformUserSsmRevisions(userId) {
  if (!userId) throw new Error("userId is required");
  const res = await authFetch(`/platform/users/${encodeURIComponent(userId)}/ssm-document-revisions`);
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Failed to load SSM revisions");
  }
  return res.json(); // { ok: true, items: [...], total }
}

/** Generate a standalone route declaration PDF for a declaration (platform admin) */
export async function generatePlatformRouteDeclarationPdf(declarationId) {
  if (!declarationId) throw new Error("declarationId is required");
  const res = await authFetch(`/platform/route-declarations/${encodeURIComponent(declarationId)}/generate-standalone-pdf`, {
    method: "POST",
  });
  const text = await res.text().catch(() => "");
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch { /* ignore */ }
  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Failed to generate route declaration PDF (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data.revision ?? data;
}

/** Send a standalone route declaration PDF revision for signing via FlexiDocs (platform admin) */
export async function sendRouteDeclarationForSignature(revisionId) {
  if (!revisionId) throw new Error("revisionId is required");
  const res = await authFetch(`/platform/document-revisions/${encodeURIComponent(revisionId)}/send-route-declaration-for-signature`, {
    method: "POST",
  });
  const text = await res.text().catch(() => "");
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch { /* ignore */ }
  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Failed to send for signature (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data.revision ?? data;
}

/** List standalone route declaration PDF revisions for a user (platform admin) */
export async function listPlatformUserRouteDeclarationRevisions(userId) {
  if (!userId) throw new Error("userId is required");
  const res = await authFetch(`/platform/users/${encodeURIComponent(userId)}/route-declaration-revisions`);
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Failed to load route declaration revisions");
  }
  return res.json(); // { ok: true, items: [...], total }
}

/** Download a specific document revision PDF (variant: unsigned|signed). Returns { blob, filename } */
export async function downloadPlatformDocumentRevision(revisionId, variant = "unsigned") {
  if (!revisionId) throw new Error("revisionId is required");
  const q = variant ? `?variant=${encodeURIComponent(variant)}` : "";
  const res = await authFetch(`/platform/document-revisions/${encodeURIComponent(revisionId)}/download${q}`);
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    let data = {};
    try { data = txt ? JSON.parse(txt) : {}; } catch { /* ignore */ }
    const msg = data?.error || data?.message || txt || `Failed to download document (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  let filename = "";
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/.exec(cd);
  if (m) {
    try { filename = decodeURIComponent(m[1]); } catch { filename = m[1]; }
  }
  if (!filename) filename = `ssm_rev_${revisionId}_${variant}.pdf`;
  return { blob, filename };
}

/**
 * Get profile data required to complete onboarding for current user.
 * GET /api/me/profile-required
 */
export async function getProfileRequired() {
  const res = await authFetch(`/me/profile-required`);
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Failed to load profile data");
  }
  return res.json();
}

/**
 * Patch profile-required fields for current user.
 * PATCH /api/me/profile-required
 */
export async function patchProfileRequired(payload = {}) {
  const res = await authFetch(`/me/profile-required`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    // ignore parse error
  }
  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Failed to save profile (${res.status})`;
    throw new Error(msg);
  }
  return data;
}

/**
 * Download (SAS) for a certificate (generated or signed), per kind.
 * GET /certificate/:slug/:kind/download?type=generated|signed[&userId][&sasMinutes]
 * Returns: { url, ownerId, kind, type }
 */
export async function certificateDownload(slug, { kind, type = "signed", userId, sasMinutes } = {}) {
  if (!kind) throw new Error("Certificate kind is required");
  const qs = new URLSearchParams();
  if (type) qs.set("type", type);                // "signed" (default) | "generated"
  if (userId) qs.set("userId", String(userId));  // manager/org admin/platform
  if (sasMinutes) qs.set("sasMinutes", String(sasMinutes));

  const res = await authFetch(
    `/certificate/${slug}/${kind}/download${qs.toString() ? `?${qs.toString()}` : ""}`
  );
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Failed to prepare certificate download");
  }
  return res.json();
}

/**
 * Upload a SIGNED certificate (PDF) for a specific kind.
 * POST /certificate/:slug/:kind/upload-signed (multipart or JSON)
 * Returns: { ok: true, kind, signedUrl, signedUrlSas }
 */
export async function uploadCertificateSigned(slug, kind, file) {
  if (!kind) throw new Error("Certificate kind is required");
  if (!file) throw new Error("No file provided");

  const fd = new FormData();
  fd.append("file", file, file.name || "signed.pdf");

  const res = await authFetch(`/certificate/${slug}/${kind}/upload-signed`, {
    method: "POST",
    body: fd, // IMPORTANT: do not set Content-Type manually
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Upload failed");
  }
  return res.json();
}

/**
 * Back-compat wrapper if older UI calls uploadCertificate(slug, file, kind?)
 * Defaults to SSM when kind not supplied.
 */
export async function uploadCertificate(slug, file, kind = "SSM") {
  return uploadCertificateSigned(slug, kind, file);
}

/**
 * Current user's certificates list (kept compatible with your Flask return shape):
 * GET /certificates/my?sasMinutes=60
 * -> [{ training:{slug,title}, uploadedUrl, uploadedUrlSas?, createdAt }]
 */
export async function listMyCertificates({ sasMinutes = 60 } = {}) {
  const res = await authFetch(`/certificates/my?sasMinutes=${encodeURIComponent(sasMinutes)}`);
  if (!res.ok) throw new Error("Failed to load certificates");
  return res.json();
}

/**
 * Certificates for a specific user (auth handled by backend).
 * GET /certificates/user/:userId?sasMinutes=60
 */
export async function listUserCertificates(userId, { sasMinutes = 60 } = {}) {
  const res = await authFetch(`/certificates/user/${userId}?sasMinutes=${encodeURIComponent(sasMinutes)}`);
  if (!res.ok) throw new Error("Failed to load user certificates");
  return res.json();
}




/* -------------------------------------------------------------------------- */
/* Mock-only helpers still used by the UI (dev conveniences)                  */
/* -------------------------------------------------------------------------- */
export const { resetTraining, clearAllMockData, sleep } = mock;
